import React, { createContext, useContext } from "react";

/**
 * Runtime side of the build-time markdown pipeline.
 *
 * Every Strapi markdown field is rendered to HTML once, in `gatsby-node.ts`
 * (see `src/helpers/markdownToHtml.ts`), and shipped in the page context as
 * `markdownHtml: { md: { <source>: <html> }, plain: { ... } }`. The provider is
 * installed by `wrapPageElement` in `gatsby-browser.js` / `gatsby-ssr.js`, so
 * any component anywhere in the tree can print its field without the markdown
 * parser: no `react-markdown`, no `rehype-raw`/parse5, no `remark-gfm`/
 * micromark in the browser bundle.
 *
 * Lookup is by the markdown source itself rather than by field path, so a
 * component keeps its old `<Markdown>{data.Text}</Markdown>` call shape.
 */

export type MarkdownVariant = "md" | "plain";
export type MarkdownHtmlMap = Partial<
  Record<MarkdownVariant, Record<string, string>>
>;

export const MarkdownHtmlContext = createContext<MarkdownHtmlMap | null>(null);

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

const escapeHtml = (value: string) =>
  value.replace(/[&<>"]/g, (char) => ESCAPES[char]);

/**
 * Resolves the pre-rendered HTML for a markdown source. Returns `null` when the
 * source was not pre-rendered - which should not happen for any field listed in
 * `markdownToHtml.ts`, and is verified by the build-time HTML diff.
 */
export const useMarkdownHtml = (
  source: unknown,
  variant: MarkdownVariant = "md"
): string | null => {
  const map = useContext(MarkdownHtmlContext);
  if (typeof source !== "string" || source === "") return null;
  const html = map && map[variant] ? map[variant]![source] : undefined;
  return html === undefined ? null : html;
};

interface MarkdownHtmlProps {
  children?: unknown;
  /** Classes for the wrapper element. Omit only where the DOM already has one. */
  className?: string;
  variant?: MarkdownVariant;
}

/**
 * Prints pre-rendered markdown inside a single `<div>`.
 *
 * XSS: the HTML comes from our own Strapi instances and was already rendered
 * raw in the browser through `rehype-raw` (CMS blocks embed iframes and raw
 * HTML on purpose), so the trust model is unchanged - the same authors, the
 * same markup, only parsed at build time instead of on every page view. A
 * sanitizer here would silently change published pages, so there is none.
 */
const MarkdownHtml: React.FC<MarkdownHtmlProps> = ({
  children,
  className,
  variant = "md",
}) => {
  const html = useMarkdownHtml(children, variant);
  if (
    html === null &&
    typeof children === "string" &&
    children !== "" &&
    typeof window === "undefined"
  ) {
    // SSR runs inside `gatsby build`, so a field nobody pre-rendered shows up in
    // the build log instead of silently shipping raw markdown to production.
    // Fix: add the Strapi field name to MD_FIELDS in helpers/markdownToHtml.ts.
    console.warn(
      `[markdown] no build-time HTML for ${variant} field: ${children.slice(0, 90)}`
    );
  }
  const __html =
    html !== null
      ? html
      : typeof children === "string"
      ? escapeHtml(children)
      : "";
  return <div className={className} dangerouslySetInnerHTML={{ __html }} />;
};

export default MarkdownHtml;
